import {DataTable} from '@/components-v2/organisms/DataTable/DataTable'
import {Modal} from '@/components-v2/organisms/Modal/Modal'
import {
  formatErrorRemark,
  getRowErrorCount,
  getSubRowParentId,
  useUploadHistoryMainRows,
  useUploadHistorySubRows,
} from '@/hooks/useUploadHistoryTableModal'
import {useMemo} from 'react'
import './UploadHistoryTableModal.scss'

/**
 * Renders the sub-rows (failed records) table for a single main row.
 */
const SubRowsTable = ({parentId, fetchSubRows, subColumns}) => {
  const {rows, isFetching, defaultSubColumns} = useUploadHistorySubRows(parentId, fetchSubRows)
  const displayedSubColumns = subColumns.length > 0 ? subColumns : defaultSubColumns

  const columns = useMemo(
    () =>
      displayedSubColumns.map(col => {
        const isStatusRemark = col.header === 'STATUS (REMARK)'
        return {
          ...col,
          cellData: isStatusRemark ? ({row}) => formatErrorRemark(row?.original) : col.cellData,
        }
      }),
    [displayedSubColumns]
  )

  return (
    <div className="rw-upload-history-sub-table">
      <DataTable
        key={rows?.length}
        columns={columns}
        data={rows}
        isLoading={isFetching}
        showColumnFilters={false}
        enableSorting={false}
        showPagination={false}
        selection="none"
        size="small"
        pageSize={rows?.length}
      />
    </div>
  )
}

/**
 * components-v2 version of UploadHistoryTableModal, built on top of the Peacock-based
 * `DataTable` and `Modal`. Accepts:
 * - mainData: an array of row objects for the main table
 * - mainColumns: an array of column definitions for the main table (columnKey/header/cellData)
 * - subColumns: an array of column definitions for the sub-table
 * - fetchSubRows: a function that fetches the failed records for a given row id
 *
 * The table expands to show the failed records for rows with errors.
 */
export const UploadHistoryTableModal = ({
  openUploadHistory,
  setOpenUploadHistory,
  mainData = [],
  mainColumns = [],
  subColumns = [],
  fetchSubRows,
  totalRecords = 0,
  onPageIndexChange = () => {},
  text = '',
}) => {
  const {mainDataSubset, tableHasExpandableRows} = useUploadHistoryMainRows(mainData)

  const renderSubComponent = row => {
    if (!(getRowErrorCount(row?.original) > 0) || !fetchSubRows) return null

    return (
      <SubRowsTable parentId={getSubRowParentId(row.original)} fetchSubRows={fetchSubRows} subColumns={subColumns} />
    )
  }

  return (
    <Modal
      showModal={openUploadHistory}
      onCloseModal={() => setOpenUploadHistory(false)}
      title="Upload History"
      text={text}
      type="L"
      showPrimaryButton={false}
      showSecondaryButton={false}
      showTertiaryButton={false}
    >
      <DataTable
        columns={mainColumns}
        data={mainDataSubset}
        totalRowsCount={totalRecords}
        onPageIndexChange={onPageIndexChange}
        manualPagination
        isExpandable={tableHasExpandableRows}
        getRowCanExpand={row => getRowErrorCount(row.original) > 0}
        renderSubComponent={renderSubComponent}
        showColumnFilters={false}
        enableSorting={false}
        selection="none"
        showPagination={false}
      />
    </Modal>
  )
}
